import { Tooltip } from '@neo/primitives/tooltip'
import { pluralize } from '@shared/utils'
import { Eye } from 'lucide-react'
import type { NodeConfig } from '../types'
import { parseVariables } from './utils'

/**
 * Node-level summary of the variables an AI node extracted over its whole run.
 *
 * The LiveKit scribe extracts variables asynchronously at node handoff, so they
 * arrive on a separate empty-text chunk that the store drops after collating
 * them into `node.aggregated_variables` — the per-response chunks carry no
 * variables. Rendering this once per AI node (from the aggregate) is how the
 * extracted variables surface; reading the individual response chunk would show
 * "None". Mirrors the v2 analysis view. Renders nothing when the node extracted
 * nothing.
 */
export const ExtractedVariablesSummary = ({
  variables,
}: {
  variables: NonNullable<NodeConfig['variables']>
}) => {
  const variablesArray = parseVariables(variables)
  if (!variablesArray.length) return null
  return (
    <div
      className="flex items-center gap-1 rounded-md py-1"
      data-testid="extracted-variables-summary"
    >
      <span className="text-xs-400 text-foreground">
        {pluralize(variablesArray.length, 'Variable')} extracted
      </span>
      <Tooltip
        className="max-h-80 max-w-[min(32rem,calc(100vw-2rem))] overflow-y-auto border border-border bg-popover p-3 text-popover-foreground"
        // @ts-expect-error tooltip accepts react node
        content={
          (
            <div className="flex flex-col gap-2 font-jetbrains text-xs-400 break-words [overflow-wrap:anywhere]">
              {variablesArray.map((variable) => (
                <div key={variable.variable_name}>
                  {variable.variable_name}: {variable.variable_value}
                </div>
              ))}
            </div>
          ) as React.ReactNode
        }
      >
        <button
          type="button"
          aria-label="View extracted variables"
          className="inline-flex size-6 shrink-0 items-center justify-center rounded text-secondary hover:bg-accent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="button-view-variables"
        >
          <Eye size={16} aria-hidden />
        </button>
      </Tooltip>
    </div>
  )
}
